// AdminLogin.jsx
import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import {
  ShieldCheck,
  User,
  Lock,
  ArrowRight,
  Loader2,
  TriangleAlert,
  CheckCircle2,
} from "lucide-react";
import Logo from "../assets/logo2.jpg";

export default function AdminLogin() {
  const BASE_URL = "https://nissi-india.com";

  const [financeYears, setFinanceYears] = useState([]);
  const [finYearId, setFinYearId] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(true);

  const [loading, setLoading] = useState(false);
  const [loadingFY, setLoadingFY] = useState(false);

  const [errMsg, setErrMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // Load finance year options from MASTER DB
  useEffect(() => {
    (async () => {
      try {
        setLoadingFY(true);
        const res = await axios.get(`${BASE_URL}/api/admin/finance-years`);
        if (res?.data?.status) {
          setFinanceYears(res.data.data || []);
        } else {
          setErrMsg(res?.data?.message || "Failed to load financial years.");
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load financial years.";
        setErrMsg(msg);
      } finally {
        setLoadingFY(false);
      }
    })();
  }, []);

  const canSubmit = useMemo(() => {
    return (
      String(finYearId).trim().length > 0 &&
      username.trim().length > 0 &&
      password.trim().length > 0 &&
      acceptTerms &&
      !loading
    );
  }, [finYearId, username, password, acceptTerms, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    setOkMsg("");

    if (!String(finYearId).trim()) {
      setErrMsg("Please select Financial Year.");
      return;
    }
    if (!username.trim() || !password.trim()) {
      setErrMsg("Please enter login details.");
      return;
    }
    if (!acceptTerms) {
      setErrMsg("Please accept the Terms & Conditions to continue.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${BASE_URL}/api/admin/login`, {
        finYearId: Number(finYearId),
        username: username.trim(),
        password: password.trim(),
        acceptTerms: true,
      });

      if (res?.data?.status) {
        setOkMsg("Login successful. Redirecting…");

localStorage.setItem("admin", JSON.stringify(res.data.admin));

// ✅ This is the FK id (tbl_Admin_Credential.admin_credential_id)
localStorage.setItem("adminId", String(res.data.admin.id));

localStorage.setItem("finYearId", String(finYearId));

localStorage.setItem("finYear", String(res.data.admin.finYear));


        setTimeout(() => {
          window.location.href = "/adminMaster/home";
        }, 700);
      } else {
        setErrMsg(res?.data?.message || "Login failed.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(148,163,184,.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(148,163,184,.25) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Glow orbs */}
      <div className="absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-indigo-600/20 blur-3xl" />
      <div className="absolute top-40 -right-40 h-[520px] w-[520px] rounded-full bg-cyan-500/15 blur-3xl" />
      <div className="absolute -bottom-40 left-1/3 h-[520px] w-[520px] rounded-full bg-amber-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-6xl px-6 py-12">
        {/* Header */}
        <header className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src={Logo}
              alt="Infraconsoft V5"
              className="h-11 w-11 rounded-xl object-cover ring-1 ring-white/10"
            />
            <div>
              <div className="text-lg font-semibold tracking-wide">
                Infraconsoft V5
              </div>
              <div className="text-xs text-white/60 -mt-0.5">
                Construction ERP • Admin Portal
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="h-4 w-4" />
            Secure access • Modernized stack
          </div>
        </header>

        {/* Body */}
        <main className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left */}
          <section className="lg:pr-10">
            <div className="flex items-center gap-3">
              <img
                src={Logo}
                alt="Infraconsoft V5"
                className="h-12 w-auto rounded-md"
              />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Infraconsoft V5
                </h2>
                <p className="text-xs text-white/60">
                  Construction ERP Platform
                </p>
              </div>
            </div>

            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs text-white/70 ring-1 ring-white/10">
              <ShieldCheck className="h-4 w-4" />
              Secure Admin Access
            </div>

            <h1 className="mt-4 text-2xl md:text-3xl font-medium leading-tight">
              Admin sign-in
            </h1>

            <p className="mt-3 text-white/60 max-w-md">
              Access centralized controls for projects, inventory, payroll, and
              approvals.
            </p>
          </section>

          {/* Right: Login card */}
          <section className="w-full">
            <div className="rounded-3xl bg-white/[0.06] ring-1 ring-white/10 shadow-2xl backdrop-blur-xl overflow-hidden">
              <div className="p-6 md:p-8 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-2xl bg-white/10 flex items-center justify-center ring-1 ring-white/10">
                    <ShieldCheck className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">Admin Login</div>
                    <div className="text-xs text-white/60">
                      Sign in to manage Infraconsoft V5
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-4">
                {/* Alerts */}
                {errMsg ? (
                  <div className="rounded-2xl bg-red-500/10 ring-1 ring-red-500/30 p-3 flex gap-3">
                    <TriangleAlert className="h-5 w-5 text-red-300 mt-0.5" />
                    <div className="text-sm text-red-100">{errMsg}</div>
                  </div>
                ) : null}

                {okMsg ? (
                  <div className="rounded-2xl bg-emerald-500/10 ring-1 ring-emerald-500/30 p-3 flex gap-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-300 mt-0.5" />
                    <div className="text-sm text-emerald-100">{okMsg}</div>
                  </div>
                ) : null}

                {/* Financial Year */}
                <div className="space-y-1.5">
                  <div className="text-xs text-white/65">Financial Year</div>
                  <div className="flex items-center gap-2 rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-3 py-2.5 focus-within:ring-white/25 transition">
                    <select
                      className="w-full bg-transparent outline-none text-white text-sm"
                      value={finYearId}
                      onChange={(e) => setFinYearId(e.target.value)}
                      disabled={loadingFY}
                    >
                      <option value="" className="text-slate-900">
                        {loadingFY ? "Loading..." : "Select financial year"}
                      </option>
                      {financeYears.map((fy) => (
                        <option
                          key={fy.fld_id}
                          value={fy.fld_id}
                          className="text-slate-900"
                        >
                          {fy.fld_financialyear}
                          {fy.fld_active ? "" : " (Inactive)"}
                        </option>
                      ))}
                    </select>

                    {loadingFY ? (
                      <Loader2 className="h-4 w-4 animate-spin text-white/60" />
                    ) : null}
                  </div>
                </div>

                {/* Username */}
                <Field
                  label="User Name"
                  icon={<User className="h-4 w-4" />}
                  value={username}
                  onChange={(v) => setUsername(v)}
                  placeholder="Enter username"
                  autoComplete="username"
                />

                {/* Password */}
                <Field
                  label="Password"
                  icon={<Lock className="h-4 w-4" />}
                  value={password}
                  onChange={(v) => setPassword(v)}
                  placeholder="Enter password"
                  type="password"
                  autoComplete="current-password"
                />

                {/* Terms */}
                <div className="flex items-start gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setAcceptTerms((s) => !s)}
                    className={[
                      "mt-0.5 h-5 w-5 rounded-md ring-1 ring-white/15 flex items-center justify-center transition",
                      acceptTerms ? "bg-white/20" : "bg-white/5",
                    ].join(" ")}
                    aria-pressed={acceptTerms}
                    aria-label="Accept Terms"
                  >
                    {acceptTerms ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : null}
                  </button>

                  <div className="text-sm text-white/70">
                    I agree to the{" "}
                    <button
                      type="button"
                      className="text-white underline underline-offset-4 hover:text-white/90"
                      onClick={() => window.open("/terms", "_blank")}
                    >
                      Terms & Conditions
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={[
                    "mt-2 w-full rounded-2xl px-4 py-3 font-semibold transition flex items-center justify-center gap-2",
                    "ring-1 ring-white/10",
                    canSubmit
                      ? "bg-white text-slate-950 hover:bg-white/90"
                      : "bg-white/20 text-white/60 cursor-not-allowed",
                  ].join(" ")}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <ArrowRight className="h-5 w-5" />
                  )}
                  {loading ? "Signing in..." : "Login"}
                </button>

                <div className="text-xs text-white/50 text-center pt-2">
                  Powered by{" "}
                  <span className="text-white/70">Infraconsoft V5</span> •
                  Construction ERP
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

function Field({
  label,
  icon,
  value,
  onChange,
  placeholder,
  type = "text",
  autoComplete,
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-white/65">{label}</div>
      <div className="flex items-center gap-2 rounded-2xl bg-white/[0.06] ring-1 ring-white/10 px-3 py-2.5 focus-within:ring-white/25 transition">
        <div className="text-white/60">{icon}</div>
        <input
          className="w-full bg-transparent outline-none text-white placeholder:text-white/35 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          type={type}
          autoComplete={autoComplete}
        />
      </div>
    </div>
  );
}
